import { useEffect, useRef } from 'react'
import { Map as MlMap, NavigationControl } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { buildRasterStyle, rasterBasemap } from '../map/basemaps'
import type { NagpurTripAlertReplay } from './useNagpurTripAlerts'

/** Same two kinds bus_alert ever carries (db/schema_bus.sql) -- tools/bus_harsh_driving.py
 * is the only writer. A Record over this union (not Record<string, string>) so every
 * lookup below is a known `string`, not `string | undefined`
 * (tsconfig's noUncheckedIndexedAccess). */
type NagpurAlertKind = 'HARSH_BRAKE' | 'HARSH_ACCEL'
const ALERT_KIND_COLOR: Record<NagpurAlertKind, string> = {
  HARSH_BRAKE: '#F43F5E',
  HARSH_ACCEL: '#F59E0B',
}
const UNKNOWN_ALERT_COLOR = '#94A3B8'

/** The trip's GPS path (site_gps_trail) plus its alert markers, on a small dedicated
 * MapLibre instance -- NOT BusRouteMap.tsx, which owns its own live /api/bus/board
 * polling and animation loop; reusing it for a one-off historical replay would mean
 * either fighting that live state or running a second, pointless live poll underneath a
 * modal showing the past. This component does nothing but draw one line + some points
 * and fit the camera to them. A separate file/component (not inlined into
 * NagpurTripAlerts.tsx) so it can be mocked out in jsdom tests the same way
 * BusRouteMap/MbmtLiveMap already are -- maplibre-gl constructs a real WebGL context,
 * which jsdom has none of. */
export function NagpurTripReplayMap({ clip }: { clip: NagpurTripAlertReplay | null }): JSX.Element {
  const container = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MlMap | null>(null)

  // Constructs once the container is mounted; torn down on unmount. Separate from the
  // caller's own data-fetch so a slow reply doesn't delay the map itself from existing.
  useEffect(() => {
    if (!container.current) return
    const basemap = rasterBasemap('osm')
    if (!basemap) return
    const map = new MlMap({
      container: container.current,
      style: buildRasterStyle(basemap),
      center: [79.09, 21.15], // Nagpur -- replaced by fitBounds once the clip loads
      zoom: 11,
      attributionControl: { compact: true },
    })
    mapRef.current = map
    map.addControl(new NavigationControl({ showCompass: false }), 'top-right')
    return () => {
      map.remove()
      mapRef.current = null
    }
  }, [])

  // Draws the line + alert markers once both the map style and the clip are ready, and
  // redraws if the clip changes (it never does for a fixed trip_id in practice, but this
  // keeps the effect correct rather than relying on that happening to be true).
  useEffect(() => {
    const map = mapRef.current
    if (!map || !clip) return
    const line = clip.points
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon))
      .map((p) => [p.lon as number, p.lat as number] as [number, number])
    const alerts = clip.alerts.filter((a) => Number.isFinite(a.lat) && Number.isFinite(a.lon))

    const draw = (): void => {
      if (!map.getSource('nagpur-replay-line')) {
        map.addSource('nagpur-replay-line', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
        map.addLayer({
          id: 'nagpur-replay-line',
          type: 'line',
          source: 'nagpur-replay-line',
          paint: { 'line-color': '#2563EB', 'line-width': 3, 'line-opacity': 0.85 },
        })
      }
      if (!map.getSource('nagpur-replay-alerts')) {
        map.addSource('nagpur-replay-alerts', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
        map.addLayer({
          id: 'nagpur-replay-alerts',
          type: 'circle',
          source: 'nagpur-replay-alerts',
          paint: {
            'circle-radius': 6,
            'circle-color': ['match', ['get', 'kind'], 'HARSH_BRAKE', ALERT_KIND_COLOR.HARSH_BRAKE, 'HARSH_ACCEL', ALERT_KIND_COLOR.HARSH_ACCEL, UNKNOWN_ALERT_COLOR],
            'circle-stroke-width': 1.5,
            'circle-stroke-color': '#fff',
          },
        })
      }
      ;(map.getSource('nagpur-replay-line') as { setData: (d: object) => void } | undefined)?.setData({
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: line },
      })
      ;(map.getSource('nagpur-replay-alerts') as { setData: (d: object) => void } | undefined)?.setData({
        type: 'FeatureCollection',
        features: alerts.map((a) => ({
          type: 'Feature' as const,
          properties: { kind: a.kind },
          geometry: { type: 'Point' as const, coordinates: [a.lon as number, a.lat as number] },
        })),
      })
      const first = line[0]
      if (line.length >= 2 && first) {
        let minLon = first[0], maxLon = first[0], minLat = first[1], maxLat = first[1]
        for (const pt of line) {
          minLon = Math.min(minLon, pt[0]); maxLon = Math.max(maxLon, pt[0])
          minLat = Math.min(minLat, pt[1]); maxLat = Math.max(maxLat, pt[1])
        }
        map.fitBounds([[minLon, minLat], [maxLon, maxLat]], { padding: 40, duration: 0 })
      } else if (line.length === 1 && first) {
        map.jumpTo({ center: first, zoom: 14 })
      }
    }

    if (map.isStyleLoaded()) draw()
    else map.once('load', draw)
  }, [clip])

  return <div ref={container} className="h-full w-full" />
}
